import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {loadPublicDeck, loadDeckSlides} from '../lib/tycho';
import events from '../lib/events';
import {MobileOnly, DesktopOnly} from '../DeviceType.jsx';
import DeckFooter from '../containers/DeckFooter.js';
import PresentationWrapper from '../containers/PresentationWrapper';
import ScrollWrapper from '../containers/ScrollWrapper';
import Clock from './Clock.jsx';
import './Deck.css';
import { isPrivateLink } from '../utils/url';

function buildDeckUrl(folderId, deckId) {
  return new URL(`/folders/${folderId}/decks/${deckId}/slides`, window.env.ADMIN_URL).href;
}

const Deck = ({
  id,
  invite,
  match,
  name,
  options,
  loadSubData,
  slideIndex,
  slide_order,
  slideReferrer,
  canEditFolder,
  folder_id,
  onOtpFailure,
}) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (match.params.deckHash) {
      loadPublicDeck(match.params.deckHash);
    }
  }, [match.params.deckHash]);

  // Loads the sub-data relating to this deck (slides, components etc), when
  // the corresponding flag has been set.
  useEffect(() => {
    let hasBeenCleanedUp = false;

    if (loadSubData && id) {
      loadDeckSlides(id)
        .then(() => {
          if (!hasBeenCleanedUp) {
            setIsLoading(false);
          }
        })
        .catch(err => {
          // This is used for the private link flow to signal that loading the
          // deck has failed, which probably means a token couldn't be obtained
          // from the OTP.
          if (onOtpFailure) {
            onOtpFailure();
          }
        });

      events.listen({deckId: id});
    }
    return function unlisten() {
      hasBeenCleanedUp = true;
      if (loadSubData) {
        events.unlisten({deckId: id});
      }
    };
  }, [loadSubData, id]);

  const presentation = (
    <React.Fragment>
      <MobileOnly>
        <ScrollWrapper slideReferrer={slideReferrer} slideIndex={slideIndex} id={id} />
      </MobileOnly>
      <DesktopOnly>
        <PresentationWrapper slideIndex={slideIndex} id={id} />
      </DesktopOnly>
    </React.Fragment>
  );

  // Don't display anything when being loaded via a private link (so that we
  // can be sure the slides have loaded and that the deck won't need to be
  // unmounted - otherwise the UI will look janky).
  if (isPrivateLink() && isLoading) {
    return null;
  }

  return (
    <div id={`deck-${id}`} className="Deck vl-flex-1">
      <div className="Deck--header vc-text-neutral">
        <h3 className="Deck--title ax-text--size-large">{name}</h3>
        <Clock locale={options.locale} timezone={options.timezone} />
      </div>
      {presentation}
      <DeckFooter
        invite={invite}
        name={name}
        slideCount={slide_order.length}
        slideIndex={slideIndex}
        canEditFolder={canEditFolder}
        deckEditUrl={buildDeckUrl(folder_id, id)}
      />
    </div>
  );
};

Deck.defaultProps = {
  options: {},
  match: {params: {}},
  slide_order: [],
  loadSubData: false,
  canEditFolder: false
};

Deck.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  invite: PropTypes.object,
  match: PropTypes.object,
  name: PropTypes.string,
  options: PropTypes.object,
  slideIndex: PropTypes.number,
  slideReferrer: PropTypes.string,
  slide_order: PropTypes.array,
  loadSubData: PropTypes.bool, // should it load its own slides, components, etc.
  canEditFolder: PropTypes.bool,
  folder_id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ])
};

export default Deck;
